import { useState } from "react";
import {
  Brain, Play, Eye, CheckCircle2, AlertCircle, Clock,
  ChevronDown, ChevronRight, Loader2, Search, Globe, FileText, Terminal,
} from "lucide-react";
import type { AgentRun, AgentStep } from "../types";

interface AgentTraceProps {
  runs: AgentRun[];
}

const STEP = {
  thought:     { label: "THINK",  color: "text-violet-400",  bg: "bg-violet-950/40",  border: "border-violet-800/50",  icon: Brain },
  action:      { label: "ACT",    color: "text-sky-400",     bg: "bg-sky-950/40",     border: "border-sky-800/50",     icon: Play },
  observation: { label: "OBS",    color: "text-emerald-400", bg: "bg-emerald-950/40", border: "border-emerald-800/50", icon: Eye },
  answer:      { label: "ANSWER", color: "text-cf-orange",   bg: "bg-orange-950/40",  border: "border-orange-800/50",  icon: CheckCircle2 },
  error:       { label: "ERROR",  color: "text-red-400",     bg: "bg-red-950/40",     border: "border-red-800/50",     icon: AlertCircle },
} as const;

const RUN_STATUS = {
  running:          { label: "Running",          dot: "bg-sky-400 animate-pulse",    text: "text-sky-400",     icon: Loader2,      spin: true },
  waiting_approval: { label: "Awaiting Approval",dot: "bg-amber-400 animate-pulse",  text: "text-amber-400",   icon: Clock,        spin: false },
  completed:        { label: "Completed",         dot: "bg-emerald-400",              text: "text-emerald-400", icon: CheckCircle2, spin: false },
  failed:           { label: "Failed",            dot: "bg-red-500",                  text: "text-red-400",     icon: AlertCircle,  spin: false },
  cancelled:        { label: "Cancelled",         dot: "bg-slate-500",                text: "text-slate-400",   icon: AlertCircle,  spin: false },
} as const;

export default function AgentTrace({ runs }: AgentTraceProps) {
  if (runs.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-1">
        <Terminal className="w-3.5 h-3.5 text-cf-orange" />
        <span className="text-[11px] font-mono font-semibold uppercase tracking-widest text-slate-500">
          Agent Reasoning Trace
        </span>
      </div>
      {runs.map((run) => <RunCard key={run.id} run={run} />)}
    </div>
  );
}

function RunCard({ run }: { run: AgentRun }) {
  const [expanded, setExpanded] = useState(true);
  const sc = RUN_STATUS[run.status] ?? RUN_STATUS.running;
  const StatusIcon = sc.icon;
  const isLive = run.status === "running" || run.status === "waiting_approval";

  return (
    <div className={`rounded-xl border bg-dark-surface overflow-hidden shadow-card transition-all ${
      isLive ? "border-dark-border-2 shadow-glow-blue" : "border-dark-border"
    }`}>
      {/* Scan line for live runs */}
      {isLive && (
        <div className="relative h-[1px] bg-dark-border-2 overflow-hidden">
          <div className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-sky-400 to-transparent animate-scan-line" />
        </div>
      )}

      {/* Run header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-dark-surface-2 transition-colors text-left"
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
        <span className="flex-1 text-xs font-mono text-slate-300 truncate">
          {run.query || "agent run"}
        </span>
        <span className={`text-[10px] font-mono font-bold ${sc.text}`}>{sc.label}</span>
        {sc.spin && <StatusIcon className="w-3.5 h-3.5 text-sky-400 animate-spin flex-shrink-0" />}
        {expanded
          ? <ChevronDown className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
        }
      </button>

      {/* Steps */}
      {expanded && (
        <div className="border-t border-dark-border">
          {run.steps.length === 0 ? (
            <div className="flex items-center gap-2 px-4 py-3 text-xs font-mono text-slate-600">
              <Loader2 className="w-3 h-3 animate-spin text-cf-orange" />
              <span>initializing agent…</span>
            </div>
          ) : (
            <div className="divide-y divide-dark-border/50">
              {run.steps.map((step, i) => (
                <StepRow key={i} step={step} index={i} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StepRow({ step, index }: { step: AgentStep; index: number }) {
  const [open, setOpen] = useState(false);
  const tc = STEP[step.type as keyof typeof STEP] ?? STEP.thought;
  const toolIcons: Record<string, typeof Search> = { search: Search, browse: Globe, summarize: FileText };
  const ToolIcon = step.tool ? (toolIcons[step.tool] ?? Play) : null;
  const hasDetail = step.content.length > 100 || !!step.toolInput;

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
          hasDetail ? "hover:bg-dark-surface-2 cursor-pointer" : "cursor-default"
        }`}
      >
        <span className="text-[10px] font-mono text-slate-700 w-5 text-right mt-0.5 flex-shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold tracking-wider border mt-0.5 ${tc.color} ${tc.bg} ${tc.border}`}>
          {tc.label}
        </div>
        <div className="flex-1 min-w-0">
          {step.tool && ToolIcon && (
            <div className="flex items-center gap-1 mb-0.5">
              <ToolIcon className="w-3 h-3 text-sky-500" />
              <span className="text-[10px] font-mono text-sky-500">{step.tool}()</span>
            </div>
          )}
          <p className={`text-xs font-mono leading-relaxed ${open ? "" : "line-clamp-2"} text-slate-400`}>
            {step.content}
          </p>
        </div>
        {hasDetail && (open
          ? <ChevronDown className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-0.5" />
          : <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-0.5" />
        )}
      </button>
      {open && (
        <div className="pl-[56px] pr-4 pb-3 space-y-2">
          <pre className="text-[11px] font-mono text-slate-400 bg-dark-bg border border-dark-border rounded-lg p-3 overflow-auto max-h-52 whitespace-pre-wrap break-words leading-relaxed">
            {step.content}
          </pre>
          {step.toolInput && (
            <pre className="text-[11px] font-mono text-emerald-500/80 bg-dark-bg border border-dark-border rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap break-words">
              {JSON.stringify(step.toolInput, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
